import {LitElement, html, css} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import global_css from './global-css.js';
import Decimal, {DecimalSource} from './break_infinity.js';

import {UnlockableLitElement} from './unlockable.js';
import {
  formatDelta,
  formatResourceCapacity,
  formatResourceValue,
} from './utils.js';
import tippy, {Instance as TippyInstance} from 'tippy.js';
import {ri} from './runner-idle.js';
import {Activity} from './activity.js';

/**
 * A resource; e.g. Stamina
 *
 */
@customElement('ri-resource')
export class Resource extends UnlockableLitElement {
  override name = 'resource';
  override displayName = this.name;
  description = 'Description';
  override _tags = new Set(['resource']);
  tooltip: TippyInstance;
  @property({type: Decimal}) _value: Decimal = new Decimal(0);
  get value() {
    return this._value;
  }
  set value(val) {
    if (this._value.lt(val)) {
      this.beforeGain(val);
    } else if (this._value.gt(val)) {
      this.beforeLoss(val);
    }
    val = val.clamp(this.minimum, this.capacity);
    this._value = val;
    if (this._value.equals(this.capacity)) {
      this.onMaxCapacity();
    }
    if (this._value.equals(this.minimum)) {
      this.onMinimum();
    }
  }
  @property({type: Decimal}) baseCapacity: Decimal = new Decimal(10);
  get capacity() {
    return this.baseCapacity;
  }
  set capacity(value) {
    this.baseCapacity = value;
  }
  get full() {
    return this.value.gte(this.capacity);
  }
  @property({type: Decimal}) minimum: Decimal = new Decimal(0);
  get vacancy() {
    return this.capacity.minus(this.value);
  }
  @property({type: Decimal}) delta = new Decimal(0);

  initialCapacity: Decimal = new Decimal(0);
  initialValue: Decimal = new Decimal(0);

  valueFormatter: (value: DecimalSource) => string = formatResourceValue;
  capacityFormatter: (capacity: DecimalSource) => string =
    formatResourceCapacity;

  beforeLoss = (_newValue: Decimal) => {};
  beforeGain = (_newValue: Decimal) => {};
  /** Callback function triggered when value is set to capacity or higher.
   *  Note that this will run each time the value is set to the capacity or above,
   *  including when it's already at max capacity!
   */
  onMaxCapacity: () => void = () => {};

  /** Callback function triggered when value is set to minimum or lower.
   *  Note that this will run each time the value is set to the minimum or below,
   *  including when it's already at minimum!
   */
  onMinimum: () => void = () => {};

  onTick: (performed: boolean, activity: Activity) => void = () => {};

  override setInitials() {
    this.initialCapacity = this.baseCapacity;
    this.initialValue = this.value;
  }

  override resetToInitials() {
    super.resetToInitials();
    this.baseCapacity = this.initialCapacity;
    this.value = this.initialValue;
  }

  constructor() {
    super();
    this.tooltip = tippy(this, {
      content: `${this.description}`,
      placement: 'top-start',
      allowHTML: true,
    });
  }
  get valueHTML() {
    return `<span class='${
      this.name
    } fancy resource-value'>${this.valueFormatter(this.value)}</span> ${
      this.displayName
    }`;
  }

  static override styles = [
    global_css,
    css`
      :host {
      }
      .ri-resource {
        display: flex;
        flex-direction: row;
        background: rgb(var(--value-color), 0.2);
        justify-content: space-between;
        width: 100%;
      }
      // shadow DOM css above overrides div[hidden]{display:none;}
      .ri-resource[hidden] {
        display: none;
      }
      .ri-resource-label,
      .ri-resource-meter {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .ri-resource-label {
        width: 50%;
      }
      .ri-resource-meter {
        width: 50%;
      }
      /* ri-meter.value {
        --color: rgb(var(--value-color));
        --height: 16px;
        --width: 100%;
      } */
      smooth-progress {
        height: 20px; //var(--height, 16px);
        width: 100%;
      }
      .delta {
        min-width: 48px;
        display: flex;
        justify-content: flex-end;
      }
    `,
  ];

  override render() {
    this.tooltip.setContent(`${this.description}`);
    return html`
      <div
        class="ri-resource"
        ?hidden=${!this.unlocked}
        @click=${this._onClick}
      >
        <div class="ri-resource-label">
          <span class="fancy">${this.displayName}</span>
        </div>
        <div class="ri-resource-meter">
          <smooth-progress
            class="value"
            .value=${new Decimal(this.value)}
            .max=${new Decimal(this.capacity)}
            .min=${new Decimal(this.minimum)}
            .text=${`${this.valueFormatter(this.value)} /
              ${this.capacityFormatter(this.capacity)}`}
          ></smooth-progress>
          <div class="delta">${formatDelta(this.delta, 0)}</div>
        </div>
      </div>
    `;
  }

  _onClick(e) {
    if (e.ctrlKey || e.metaKey) {
      this.hiddenByPlayer = !this.hiddenByPlayer;
      if (this.hiddenByPlayer) {
        this.classList.add('hiddenByPlayer');
      } else {
        this.classList.remove('hiddenByPlayer');
      }
      ri.requestUpdate();
      return;
    }
  }
}

@customElement('ri-meter')
export class Meter extends LitElement {
  @property({type: Decimal, attribute: false}) value = new Decimal(0);
  @property({type: Decimal, attribute: false}) capacity = new Decimal(0);
  @property({type: Decimal, attribute: false}) minimum = new Decimal(0);

  static override styles = css`
    meter::-webkit-meter-optimum-value {
      background: var(--color, green);
    }
    meter::-webkit-meter-bar {
      background: rgb(var(--none-color, none), 1);
      height: var(--height, 16px);
    }
    meter {
      height: var(--height, 16px);
      width: var(--width, 128px);
      vertical-align: baseline;
    }
  `;
  override render() {
    return html`
      <meter
        value=${this.value.toNumber()}
        max=${this.capacity.toNumber()}
        min=${this.minimum.toNumber()}
      ></meter>
    `;
  }
}
